/**
 * Edit Sent modal – creative column with AI (parity with bulk edit).
 */

import { useCallback, useMemo } from 'react';
import { MessageSquare } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { DropLabel } from '@/features/campaign-texts/components/PerDropGroupSelector';
import { useAuth } from '@/providers/AuthProvider';
import type { PerformanceTag } from '@/features/smswiser-campaigns/components/drop-modals/drop-modals.types';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { ColumnHeader } from '@/features/smswiser-campaigns/components/ScaleDropModal/shared';
import type { ScaleDropModalProps } from '@/features/smswiser-campaigns/components/ScaleDropModal/ScaleDropModal.types';

import { useAiSenderGeneration } from '@/features/smswiser-campaigns/components/ScaleDropModal/modals/scale-or-edit/creative/useAiSenderGeneration';
import { useAiTextGeneration } from '@/features/smswiser-campaigns/components/ScaleDropModal/modals/scale-or-edit/creative/useAiTextGeneration';

import { EditSentDropCreativeSendersBlock, EditSentDropCreativeTextsBlock } from './edit-creative';
import type { useEditSentDropForm } from './useEditSentDropForm';

const NOOP_ADD = async (_t: string[]) => {};

function labelsFromDrops(drops: PreparedDrop[]): DropLabel[] {
  return drops.map((d, i) => ({
    id: d.id,
    label: (d.sentTitle ?? d.titleBase ?? `Drop ${i + 1}`).trim() || `Drop ${i + 1}`,
  }));
}

interface Props {
  drops: PreparedDrop[];
  form: ReturnType<typeof useEditSentDropForm>;
  isBusy: boolean;
  countryKey: string;
  onAddSender?: ScaleDropModalProps['onAddSender'];
  onAddText?: ScaleDropModalProps['onAddText'];
  onDeleteTextsBulk?: ScaleDropModalProps['onDeleteTextsBulk'];
  tagsByTextId?: Record<string, PerformanceTag[]>;
  tagColors?: Record<string, string>;
  smsTextsWithIds?: ScaleDropModalProps['smsTextsWithIds'];
}

export function EditSentDropCreativeColumn(props: Props) {
  const { drops, form, isBusy, countryKey, onAddSender, onAddText, onDeleteTextsBulk, tagsByTextId, tagColors, smsTextsWithIds } = props;
  const dropCount = drops.length;
  const dropLabels = useMemo(() => labelsFromDrops(drops), [drops]);
  const { hasPermission } = useAuth();
  const showAi = Boolean(onAddText) && hasPermission('campaign_texts.generate');
  const showAiSenders = Boolean(onAddSender) && hasPermission('campaign_senders.generate');

  const onSaveGeneratedPerDrop = useCallback(
    async (batches: string[][]) => {
      form.setPerDropSmsTexts(batches);
    },
    [form],
  );

  const ai = useAiTextGeneration({
    countryKey,
    onAddTexts: onAddText ?? NOOP_ADD,
    onSaveGeneratedAndSelectOnly: form.saveGeneratedAndSelectOnly,
    onMarkTextsForHide: form.markTextsForHide,
    onSaveGeneratedPerDrop: dropCount > 1 ? onSaveGeneratedPerDrop : undefined,
    onAiGeneratedDeselectOld: form.clearTextsSelection,
    onAfterSave: () => {
      form.setShowTexts(false);
      form.setShowSenders(true);
    },
  });

  const onSaveGeneratedSendersPerDrop = useCallback(
    async (batches: string[][]) => {
      form.setPerDropSenderNames(batches);
    },
    [form],
  );

  const aiSenders = useAiSenderGeneration({
    countryKey,
    onAddSenders: onAddSender
      ? async (s) => {
          for (const n of s) await onAddSender(n);
        }
      : async () => {},
    onSaveGeneratedAndSelectOnly: form.saveGeneratedSendersAndSelectOnly,
    onMarkSendersForHide: form.markSendersForHide,
    onSaveGeneratedPerDrop: dropCount > 1 ? onSaveGeneratedSendersPerDrop : undefined,
    onPerDropSendersApplied: (batches) => form.setPerDropSenderNames(batches),
    onAiGeneratedDeselectOld: form.clearSendersSelection,
    onAfterSave: () => {
      form.setShowSenders(false);
      form.setShowTexts(true);
    },
  });

  const hasAiSenders = Boolean(
    (aiSenders.perDropBatches && aiSenders.perDropBatches.length > 0) || aiSenders.generatedSenders.length > 0,
  );
  const hasAiTexts = Boolean(
    (ai.perDropBatches && ai.perDropBatches.length > 0) || ai.generatedTexts.length > 0,
  );
  const sendersTakeFullSpace = hasAiSenders || !form.showTexts;
  const textsTakeFullSpace = hasAiTexts || !form.showSenders;

  return (
    <>
      <ColumnHeader icon={MessageSquare} title="Creative" subtitle="Senders & Texts" />
      <div
        className={cn(
          'scale-creative-grid min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-3',
          sendersTakeFullSpace && 'scale-creative-grid-ai-senders',
          textsTakeFullSpace && !sendersTakeFullSpace && 'scale-creative-grid-ai-texts',
          !form.showSenders && form.showTexts && 'scale-creative-grid-senders-collapsed',
          form.showSenders && !form.showTexts && 'scale-creative-grid-texts-collapsed',
        )}
      >
        <EditSentDropCreativeSendersBlock
          form={form}
          isBusy={isBusy}
          countryKey={countryKey}
          dropCount={dropCount}
          dropLabels={dropLabels}
          showAiSenders={showAiSenders}
          onAddSender={onAddSender}
          aiSenders={aiSenders}
        />
        <EditSentDropCreativeTextsBlock
          form={form}
          isBusy={isBusy}
          countryKey={countryKey}
          dropCount={dropCount}
          dropLabels={dropLabels}
          showAi={showAi}
          onAddText={onAddText}
          onDeleteTextsBulk={onDeleteTextsBulk}
          tagsByTextId={tagsByTextId}
          tagColors={tagColors}
          smsTextsWithIds={smsTextsWithIds}
          ai={ai}
        />
      </div>
    </>
  );
}
